import {ColorSchemeType, Flag} from 'platform/components';
import {
  Box,
  Hide,
  HStack,
  Icon,
  Show,
  Text,
  ThemeColorPath,
  ThemeIconKey,
  VStack,
} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {CustomerMessageChannel, CustomerMessageResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';
import {MessageText} from '@dms/shared';

import {formatFileSize, parseDate} from 'shared/web';

interface CustomerMessageBubbleProps {
  message: CustomerMessageResponseBody;
}

export function CustomerMessageBubble(props: CustomerMessageBubbleProps) {
  const {message} = props;
  const formatDateTime = useDateTimeFormatter();

  const isOutgoing = message.direction === 'OUTGOING';
  const isEmail = message.channel === 'email';

  return (
    <HStack justify={isOutgoing ? 'flex-end' : 'flex-start'}>
      <Box
        maxWidth={isEmail ? '100%' : '70%'}
        minWidth={40}
        padding={3}
        borderRadius="medium"
        backgroundColor={isOutgoing ? 'palettes.blue.10.100' : 'palettes.neutral.20.100'}
      >
        <VStack spacing={2}>
          <HStack spacing={2} align="center" justify="space-between">
            <HStack spacing={1} align="center">
              <Icon
                value={getChannelIcon(message.channel)}
                size={4}
                color={getChannelIconColor(message.channel)}
              />
              <Flag
                size="small"
                isSubtle
                colorScheme={getChannelColorScheme(message.channel)}
                label={getChannelLabel(message.channel)}
              />
            </HStack>
            <Text size="xSmall" color="secondary">
              {message.contact}
            </Text>
          </HStack>
          <Show when={isNotNilOrEmpty(message.subject)}>
            <Text size="small" alternative>
              {message.subject}
            </Text>
          </Show>
          <Show when={isEmail}>
            <Box
              height={60}
              width={160}
              maxWidth="100%"
              padding={4}
              backgroundColor="general.white"
              overflowY="auto"
            >
              <MessageText htmlData={message.content} />
            </Box>
          </Show>
          <Hide when={isEmail}>
            <Text size="small">{message.content}</Text>
          </Hide>
          {message.attachments.map((attachment) => (
            <HStack key={attachment.uri} spacing={1} align="center">
              <Icon value="action/description" size={4} color="text.secondary" />
              <Text size="xSmall" color="secondary">
                {isNotNilOrEmpty(attachment.size) && attachment.size
                  ? `${attachment.filename} (${formatFileSize(attachment.size)})`
                  : attachment.filename}
              </Text>
            </HStack>
          ))}
          <Text size="xSmall" color="tertiary" align="right">
            {formatDateTime('dateTimeShort', parseDate(message.createdAt))}
          </Text>
        </VStack>
      </Box>
    </HStack>
  );
}

function getChannelColorScheme(channel: CustomerMessageChannel): ColorSchemeType {
  return match(channel)
    .with('email', () => 'blue' as const)
    .with('sms', () => 'teal' as const)
    .with('whatsapp', () => 'green' as const)
    .exhaustive();
}

function getChannelIcon(channel: CustomerMessageChannel): ThemeIconKey {
  return match<CustomerMessageChannel, ThemeIconKey>(channel)
    .with('email', () => 'communication/email')
    .with('sms', () => 'communication/textsms')
    .with('whatsapp', () => 'communication/comment')
    .exhaustive();
}

function getChannelIconColor(channel: CustomerMessageChannel): ThemeColorPath {
  return match<CustomerMessageChannel, ThemeColorPath>(channel)
    .with('email', () => 'palettes.blue.70.100')
    .with('sms', () => 'palettes.teal.70.100')
    .with('whatsapp', () => 'palettes.green.70.100')
    .exhaustive();
}

function getChannelLabel(channel: CustomerMessageChannel): string {
  return match(channel)
    .with('email', () => i18n.t('general.labels.email'))
    .with('sms', () => i18n.t('general.labels.sms'))
    .with('whatsapp', () => i18n.t('general.labels.whatsapp'))
    .exhaustive();
}
